import { useState, useEffect, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { AlertTriangle, ArrowUpRight, CalendarDays, Check, CheckCircle, Copy, Loader2, Monitor } from 'lucide-react'
import { Button as AriaButton } from 'react-aria-components'
import { toast } from 'sonner'
import { Button } from '@/components/base/buttons'
import { Checkbox } from '@/components/ui/Checkbox'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalTrigger } from '@/components/ui/Modal'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { loadFacebookSDK, launchEmbeddedSignup } from '@/lib/facebook-sdk'
import { isSafari } from '@/lib/utils'
import {
  useWhatsAppStatus,
  useConnectWhatsAppNumber,
  useResendOtp,
  useVerifyNumber,
  usePreverifyStatus,
  usePreverifyStart,
  preverifyCooldownFromError,
  reportSignupEvent,
} from '@/hooks/api/useWhatsAppSignup'
import type { TwilioSenderStatus } from '@/hooks/api/useWhatsAppSignup'
import { useAssistant } from '@/hooks/api/useAssistants'
import { BusinessProfileForm } from './BusinessProfileForm'
import { VerificationRedirectCard } from './VerificationRedirectCard'

const BASE = 'resources.assistants.whatsapp_signup'
const HOW_IT_WORKS_STEPS = [1, 2, 3, 4, 5, 6, 7, 8] as const

function WhatsAppLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <path
        fill="#25D366"
        d="M16 0C7.163 0 0 7.163 0 16c0 2.822.735 5.583 2.135 8.017L0 32l8.185-2.146A15.94 15.94 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0Z"
      />
      <path
        fill="#fff"
        d="M23.472 19.339c-.365-.183-2.159-1.066-2.493-1.187-.334-.122-.577-.183-.82.183-.243.365-.94 1.186-1.152 1.43-.213.243-.426.274-.79.091-.365-.183-1.543-.569-2.938-1.812-1.086-.968-1.819-2.163-2.032-2.528-.213-.365-.023-.562.16-.744.164-.164.365-.426.548-.639.183-.213.243-.365.365-.608.122-.243.061-.456-.03-.639-.091-.183-.82-1.977-1.124-2.706-.296-.71-.596-.614-.82-.625-.213-.01-.456-.012-.699-.012-.243 0-.638.091-.972.456-.334.365-1.276 1.247-1.276 3.041 0 1.795 1.306 3.529 1.488 3.772.183.243 2.569 3.925 6.226 5.503.87.376 1.548.6 2.077.768.872.278 1.667.239 2.294.145.7-.104 2.159-.883 2.463-1.735.304-.852.304-1.583.213-1.735-.091-.152-.334-.243-.699-.426Z"
      />
    </svg>
  )
}

function MetaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 287 191" className={className} aria-hidden="true">
      <path
        fill="#0668E1"
        d="M31.06 126.4c0 11 2.42 19.44 5.58 24.55 4.15 6.7 10.34 9.54 16.65 9.54 8.14 0 15.58-2.02 29.92-21.85 11.49-15.9 25.03-38.22 34.14-52.2l15.42-23.7c10.72-16.47 23.13-34.79 37.36-47.21 11.62-10.14 24.14-15.77 36.75-15.77 21.16 0 41.32 12.26 56.75 35.28 16.9 25.2 25.1 56.94 25.1 89.7 0 19.47-3.85 33.79-10.37 45.1-6.31 10.94-18.6 21.86-39.28 21.86v-31.14c17.72 0 22.13-16.28 22.13-34.9 0-26.55-6.19-56-19.82-77.06-9.68-14.94-22.22-24.07-36.03-24.07-14.94 0-26.96 11.27-40.48 31.37-7.19 10.68-14.57 23.7-22.85 38.4l-9.1 16.11c-18.29 32.4-22.92 39.79-32.05 51.96-15.99 21.33-29.63 29.42-47.65 29.42-21.36 0-34.86-9.25-43.22-23.19C1.06 156.02 0 141.83 0 125.66l31.06.74Z"
      />
      <path
        fill="#0080FB"
        d="M24.49 37.3C38.8 15.23 59.46 0 83.15 0c13.72 0 27.36 4.06 41.61 15.7 15.59 12.72 32.21 33.68 52.95 68.24l7.44 12.4c17.95 29.9 28.16 45.29 34.14 52.55 7.7 9.32 13.09 12.1 20.1 12.1 17.72 0 22.13-16.28 22.13-34.9l27.4-.87c0 19.47-3.85 33.79-10.37 45.1-6.31 10.94-18.6 21.86-39.28 21.86-12.86 0-24.25-2.8-36.85-14.68-9.7-9.13-21.02-25.35-29.73-39.91l-25.94-43.32c-13.02-21.75-24.97-37.94-31.9-45.31-7.44-7.9-17-17.44-32.26-17.44-12.35 0-22.85 8.66-31.63 21.9l-26.47-16.13Z"
      />
      <path
        fill="#4E82EA"
        d="M82.6 31.4c-12.35 0-22.85 8.66-31.63 21.9-12.42 18.68-20.03 46.53-20.03 73.24 0 11 2.42 19.44 5.58 24.55L10.72 168.4C1.06 156.02 0 141.83 0 125.66c0-30.87 8.47-63.05 24.49-87.8C38.8 15.98 59.46.7 83.15.7l-.55 30.7Z"
      />
    </svg>
  )
}


// eslint-disable-next-line react-refresh/only-export-components -- exported for tests
export function apiErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const msg = (detail[0] as { msg?: unknown } | undefined)?.msg
      if (typeof msg === 'string') return msg
    }
  }
  return err instanceof Error ? err.message : 'Failed'
}

interface WhatsAppEmbeddedSignupProps {
  assistantId: number
}

export function WhatsAppEmbeddedSignup({ assistantId }: WhatsAppEmbeddedSignupProps) {
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [verifyAttempts, setVerifyAttempts] = useState(0)
  // The wait is the server's. This only renders it, re-seeded from every poll and
  // from the 429 body — a client timer is never the authority.
  const [preverifyWait, setPreverifyWait] = useState(0)
  const [verifying, setVerifying] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0)
  const [resendNow, setResendNow] = useState(Date.now())
  const [senderStatusSnapshot, setSenderStatusSnapshot] = useState<TwilioSenderStatus | null>(null)
  const [displayNameAck, setDisplayNameAck] = useState(false)
  const [gateOpen, setGateOpen] = useState(false)

  const RETRY_STORAGE_KEY = `wa_retry_${assistantId}`
  const WAIT_STORAGE_KEY = `wa_connect_wait_${assistantId}`
  const MAX_RETRIES = 3
  const RETRY_COOLDOWN_MS = 3 * 60 * 1000   // 3 min between attempts
  const LOCKOUT_MS = 60 * 60 * 1000          // 1 hr after exhausting retries
  // Meta/Twilio keep provisioning for minutes after the popup closes; an early
  // retry deletes and re-registers the sender and makes it worse. Hold the
  // retry button for 20 minutes after a connect attempt.
  const RETRY_UNLOCK_DELAY_MS = 20 * 60 * 1000

  const getRetryState = () => {
    try { return JSON.parse(localStorage.getItem(RETRY_STORAGE_KEY) ?? '{}') } catch { return {} }
  }
  const retryState = getRetryState()
  const retryCount: number = retryState.count ?? 0
  const lockedUntil: number = retryState.lockedUntil ?? 0
  const isRetryLocked = Date.now() < lockedUntil
  const retriesLeft = MAX_RETRIES - retryCount

  const recordRetry = () => {
    const next = retryCount + 1
    const locked = next >= MAX_RETRIES ? Date.now() + LOCKOUT_MS : 0
    localStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify({ count: next, lockedUntil: locked }))
  }

  const { t } = useTranslation()
  const isDesktop = useBreakpoint('md')
  const { data: status, isLoading: statusLoading } = useWhatsAppStatus(assistantId)
  const { data: assistant } = useAssistant(String(assistantId))
  const { data: preverify } = usePreverifyStatus(assistantId)
  const startPreverify = usePreverifyStart()
  const connectWhatsAppNumber = useConnectWhatsAppNumber()
  const resendOtp = useResendOtp()
  const verifyNumber = useVerifyNumber()

  const metaAppId = import.meta.env.VITE_META_APP_ID
  const metaConfigId = import.meta.env.VITE_META_CONFIG_ID
  const metaSolutionId = import.meta.env.VITE_META_SOLUTION_ID
  const walkthroughUrl = import.meta.env.VITE_WHATSAPP_WALKTHROUGH_URL as string | undefined
  const faqUrl = import.meta.env.VITE_WHATSAPP_FAQ_URL as string | undefined
  const calendlyUrl = import.meta.env.VITE_WHATSAPP_CALENDLY_URL as string | undefined

  const isConnected = status?.connected
  // Meta rejects display names that start with a lowercase letter.
  const trimmedName = assistant?.name?.trim()
  const displayName = trimmedName ? trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1) : undefined

  useEffect(() => {
    console.log('WhatsAppEmbeddedSignup mounted', { metaAppId, metaConfigId, metaSolutionId })
    if (metaAppId) {
      loadFacebookSDK(metaAppId).then(() => {
        console.log('Facebook SDK loaded')
        setSdkLoaded(true)
      })
    }
  }, [metaAppId, metaConfigId, metaSolutionId])

  useEffect(() => {
    if (resendCooldownUntil <= 0) return
    const tick = setInterval(() => setResendNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [resendCooldownUntil])

  // Fix #17: clear verifying state once connected
  useEffect(() => {
    if (status?.connected) {
      setVerifying(false)
      localStorage.removeItem(WAIT_STORAGE_KEY)
    }
  }, [status?.connected, WAIT_STORAGE_KEY])

  // Arm the 20-minute retry hold when the failure card first appears (stamp
  // survives reloads; also set at connect-attempt time in handleConnect).
  useEffect(() => {
    if (!status?.can_programmatic_retry || status.connected) return
    let start = Number(localStorage.getItem(WAIT_STORAGE_KEY) ?? 0)
    if (!start) {
      start = Date.now()
      localStorage.setItem(WAIT_STORAGE_KEY, String(start))
    }
    const until = start + RETRY_UNLOCK_DELAY_MS
    if (until > Date.now()) {
      setResendCooldownUntil((prev) => Math.max(prev, until))
      setResendNow(Date.now())
    }
  }, [status?.can_programmatic_retry, status?.connected, WAIT_STORAGE_KEY, RETRY_UNLOCK_DELAY_MS])

  const handleConnect = useCallback(async () => {
    if (!sdkLoaded || !metaConfigId) {
      toast.error(t(`${BASE}.toast.sdk_not_loaded`))
      return
    }

    setConnecting(true)
    reportSignupEvent({ assistant_id: assistantId, event: 'START', data: {} })
    try {
      const preverifiedId =
        preverify?.status === 'verified' ? preverify.preverified_id ?? undefined : undefined
      const result = await launchEmbeddedSignup(
        metaConfigId,
        metaSolutionId,
        displayName ? { displayName } : undefined,
        undefined,
        (event, data) => reportSignupEvent({ assistant_id: assistantId, event, data }),
        preverifiedId,
      )

      await connectWhatsAppNumber.mutateAsync({
        assistant_id: assistantId,
        waba_id: result.wabaId,
        phone_number_id: result.phoneNumberId,
      })

      toast.success(t(`${BASE}.toast.number_added`))
    } catch (err) {
      const message = apiErrorMessage(err)
      if (message.includes('cancelled')) {
        toast.error(t(`${BASE}.toast.signup_cancelled`))
      } else {
        toast.error(t(`${BASE}.toast.connection_failed`, { message }))
      }
    } finally {
      // Start the retry hold at attempt time — provisioning continues server-side.
      localStorage.setItem(WAIT_STORAGE_KEY, String(Date.now()))
      setConnecting(false)
    }
  }, [
    t,
    WAIT_STORAGE_KEY,
    sdkLoaded,
    metaConfigId,
    metaSolutionId,
    assistantId,
    connectWhatsAppNumber,
    displayName,
    preverify?.status,
    preverify?.preverified_id,
  ])

  const resendSecondsLeft = Math.max(0, Math.ceil((resendCooldownUntil - resendNow) / 1000))

  useEffect(() => {
    if (preverify?.retry_after_seconds === undefined) return
    setPreverifyWait(preverify.retry_after_seconds)
  }, [preverify?.retry_after_seconds, preverify?.updated_at])

  useEffect(() => {
    if (preverifyWait <= 0) return
    const timer = setTimeout(() => setPreverifyWait((seconds) => Math.max(0, seconds - 1)), 1000)
    return () => clearTimeout(timer)
  }, [preverifyWait])

  const sideCard = (
    <div className="space-y-4">
      {/* Tutorial video card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <a href={walkthroughUrl || '#'} target={walkthroughUrl ? '_blank' : undefined} rel="noopener noreferrer" className="flex cursor-pointer items-start gap-4">
          <span className="flex-shrink-0">
            <svg width="80" height="64" viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0.5H70C75.2467 0.5 79.5 4.7533 79.5 10V54C79.5 59.2467 75.2467 63.5 70 63.5H10C4.7533 63.5 0.5 59.2467 0.5 54V10C0.5 4.7533 4.7533 0.5 10 0.5Z" fill="url(#thumb_gradient)"/>
              <path d="M10 0.5H70C75.2467 0.5 79.5 4.7533 79.5 10V54C79.5 59.2467 75.2467 63.5 70 63.5H10C4.7533 63.5 0.5 59.2467 0.5 54V10C0.5 4.7533 4.7533 0.5 10 0.5Z" stroke="#EBEBEA"/>
              <path d="M37.1978 35.65V27.92c0-.37.397-.6.7-.4l5.86 3.86c.28.19.28.62 0 .8l-5.86 3.86c-.303.2-.7-.027-.7-.4Z" fill="white"/>
              <defs>
                <linearGradient id="thumb_gradient" x1="40" y1="0" x2="40" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#C569CF"/>
                  <stop offset="1" stopColor="#EE609C"/>
                </linearGradient>
              </defs>
            </svg>
          </span>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{t(`${BASE}.side.tutorial_title`)}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t(`${BASE}.side.tutorial_subtitle`)}</p>
          </div>
        </a>
      </div>

      {/* Help links card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-col gap-5">
          <ModalTrigger>
            <AriaButton className="flex cursor-pointer items-center gap-3.5 text-sm font-medium text-gray-600 outline-none hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
              <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 00-4 10.5c.6.5 1 1.5 1 2.5h6c0-1 .4-2 1-2.5A6 6 0 0012 3z"/></svg>
              {t(`${BASE}.side.how_it_works`)}
            </AriaButton>
            <Modal size="lg">
              <ModalContent>
                {({ close }) => (
                  <>
                    <ModalHeader onClose={close}>{t(`${BASE}.how_it_works.title`)}</ModalHeader>
                    <ModalBody>
                      <p className="text-sm italic text-gray-600 dark:text-gray-400">
                        {t(`${BASE}.how_it_works.intro`)}
                      </p>
                      <ol className="mt-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
                        {HOW_IT_WORKS_STEPS.map((n) => (
                          <li key={n} className="flex gap-3">
                            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-medium text-white dark:bg-gray-100 dark:text-gray-900">
                              {n}
                            </span>
                            <span>{t(`${BASE}.how_it_works.step_${n}`)}</span>
                          </li>
                        ))}
                      </ol>
                    </ModalBody>
                  </>
                )}
              </ModalContent>
            </Modal>
          </ModalTrigger>
          <a href={faqUrl || '#'} target={faqUrl ? '_blank' : undefined} rel="noopener noreferrer" className="flex items-center gap-3.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
            <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 01-8 8H4l2-3a8 8 0 1115-5z"/><path d="M10.5 10a1.8 1.8 0 013.5.6c0 1.2-1.8 1.6-1.8 2.6"/><circle cx="12.2" cy="16" r=".4" fill="currentColor"/></svg>
            {t(`${BASE}.side.read_faq`)}
          </a>
        </div>
      </div>

      {/* Getting stuck card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 flex-none text-gray-500" />
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{t(`${BASE}.side.stuck_title`)}</p>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          {t(`${BASE}.side.stuck_description`)}
        </p>
        {calendlyUrl && (
          <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:underline">
            {t(`${BASE}.side.schedule_meeting`)}
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        )}
      </div>
    </div>
  )

  if (statusLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-64 animate-pulse rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/20" />
        <div className="h-64 animate-pulse rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/20" />
      </div>
    )
  }

  if (isConnected && status) {
    // ponytail: technical fields logged for dev inspection only; not shown in UI.
    console.debug('[whatsapp status]', assistantId, status)
    return (
      <>
      <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-lg border border-green-100 bg-green-50/50 p-4 dark:border-green-800/50 dark:bg-green-900/10">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
            <p className="text-sm text-gray-900 dark:text-gray-100">{t(`${BASE}.connected.title`)}</p>
          </div>
          {status.phone_number && (
            <div className="mt-3">
              <Button
                type="button"
                color="primary"
                size="sm"
                className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                onPress={() =>
                  window.open(
                    `https://wa.me/${status.phone_number!.replace('+', '')}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                {t(`${BASE}.connected.send_whatsapp`)}
              </Button>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white/60 p-4 dark:border-gray-700 dark:bg-gray-900/30">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t(`${BASE}.connected.widget_title`)}</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {status.phone_number && (
              <>
                <Trans
                  i18nKey={`${BASE}.connected.widget_description`}
                  values={{ number: status.phone_number }}
                  components={{ mono: <span className="font-mono" /> }}
                />{' '}
              </>
            )}
            {t('resources.widgets.whatsapp_signup.managed_in_hub')}
          </p>
          <div className="mt-3">
            <Button
              color="primary"
              size="sm"
              className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
              href="/widgets"
            >
              {t('resources.widgets.whatsapp_signup.open_hub')}
            </Button>
          </div>
        </div>
      </div>
      <div className="order-first lg:order-none">{sideCard}</div>
      </div>
      <BusinessProfileForm assistantId={assistantId} />
      </>
    )
  }

  if (status && !status.connected && (status.awaiting_otp || status.sender_offline || status.sender_added || status.can_programmatic_retry || resendOtp.isPending)) {
    const isOffline = !!status.sender_offline

    if (status.can_programmatic_retry) {
      return (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
          <div className="self-start rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {resendSecondsLeft > RETRY_COOLDOWN_MS / 1000
                ? t(`${BASE}.retry.wait_title`)
                : t(`${BASE}.retry.title`)}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {resendSecondsLeft > RETRY_COOLDOWN_MS / 1000
                ? t(`${BASE}.retry.wait_description`)
                : t(`${BASE}.retry.description`)}
            </p>
            {senderStatusSnapshot?.blocked_reason || isRetryLocked ? (
              <div className="mt-3">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {senderStatusSnapshot?.blocked_reason
                    ? (senderStatusSnapshot.remediation ?? t(`${BASE}.retry.not_possible`))
                    : t(`${BASE}.retry.too_many_attempts`)}
                </p>
                {isRetryLocked && calendlyUrl && (
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100">
                    {t(`${BASE}.retry.schedule_meeting`)} →
                  </a>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isLoading={resendOtp.isPending}
                  isDisabled={resendSecondsLeft > 0 || resendOtp.isPending}
                  onPress={async () => {
                    try {
                      const snap = await resendOtp.mutateAsync({ assistant_id: assistantId })
                      setSenderStatusSnapshot(snap)
                      if (!snap.blocked_reason) {
                        recordRetry()
                        setResendCooldownUntil(Date.now() + RETRY_COOLDOWN_MS)
                        setResendNow(Date.now())
                        toast.success(t(`${BASE}.toast.retry_started`))
                      }
                    } catch (e) {
                      toast.error(t(`${BASE}.toast.retry_failed`, { message: apiErrorMessage(e) }))
                    }
                  }}
                >
                  {resendSecondsLeft > 90
                    ? t(`${BASE}.retry.button_wait`, { minutes: Math.ceil(resendSecondsLeft / 60) })
                    : resendSecondsLeft > 0
                      ? t(`${BASE}.retry.button_cooldown`, { seconds: resendSecondsLeft })
                      : t(`${BASE}.retry.button`)}
                </Button>
                {retryCount > 0 && retriesLeft > 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    {t(`${BASE}.retry.attempts_remaining`, { count: retriesLeft })}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="order-first lg:order-none">{sideCard}</div>
        </div>
        </>
      )
    }

    const steps: Array<[string, boolean]> = [
      [t(`${BASE}.progress.registering`), status.sender_added],
      [t(`${BASE}.progress.attaching`), status.attached_to_messaging_service],
      [t(`${BASE}.progress.verifying`), status.sender_online],
    ]

    // Fix #17: show optimistic connecting spinner after OTP success
    if (verifying && !status.connected) {
      return (
        <>
          <div className="max-w-xl">
          <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/40 p-6 dark:border-blue-900/40 dark:bg-blue-950/20">
            <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">{t(`${BASE}.progress.connecting`)}</p>
          </div>
        </div>
        </>
      )
    }

    return (
      <>
      <div className="max-w-xl">
        {isOffline ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t(`${BASE}.offline.title`)}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t(`${BASE}.offline.description`)}
              </p>


              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isDisabled={resendSecondsLeft > 0 || resendOtp.isPending || !!senderStatusSnapshot?.blocked_reason}
                  isLoading={resendOtp.isPending}
                  onPress={async () => {
                    try {
                      const snap = await resendOtp.mutateAsync({ assistant_id: assistantId })
                      setSenderStatusSnapshot(snap)
                      // Fix #21: only set cooldown when not blocked
                      if (!snap.blocked_reason) {
                        setResendCooldownUntil(Date.now() + 60_000)
                        setResendNow(Date.now())
                        toast.success(t(`${BASE}.toast.call_requested`))
                      } else {
                        toast.error(snap.remediation ?? t(`${BASE}.toast.retry_not_possible`))
                      }
                    } catch (e) {
                      toast.error(t(`${BASE}.toast.retry_failed`, { message: apiErrorMessage(e) }))
                    }
                  }}
                >
                  {resendSecondsLeft > 0
                    ? t(`${BASE}.offline.request_call_cooldown`, { seconds: resendSecondsLeft })
                    : t(`${BASE}.offline.request_call`)}
                </Button>
              </div>

              {senderStatusSnapshot?.blocked_reason && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {senderStatusSnapshot.remediation ?? t(`${BASE}.offline.cannot_retry`)}
                </p>
              )}
            </div>

            <VerificationRedirectCard assistantId={assistantId} />

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t(`${BASE}.offline.code_title`)}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t(`${BASE}.offline.code_description`)}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <input
                  id="wa-otp-resume"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-32 rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isDisabled={otpCode.length < 6}
                  isLoading={verifyNumber.isPending}
                  onPress={async () => {
                    try {
                      await verifyNumber.mutateAsync({ assistant_id: assistantId, code: otpCode })
                      toast.success(t(`${BASE}.toast.verified`))
                      setOtpCode('')
                      setVerifying(true)
                    } catch (e) {
                      toast.error(t(`${BASE}.toast.verification_failed`, { message: apiErrorMessage(e) }))
                    }
                  }}
                >
                  {t(`${BASE}.offline.submit_code`)}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-6 dark:border-blue-900/40 dark:bg-blue-950/20">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {t(`${BASE}.progress.title`)}
              </p>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t(`${BASE}.progress.description`)}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {steps.map(([label, done]) => (
                <li key={label} className="flex items-center gap-2.5">
                  {done ? (
                    <Check className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                  ) : (
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400 dark:text-gray-500" />
                  )}
                  <span className={done ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
            {status.awaiting_otp && (
              <div className="mt-5 border-t border-blue-100 pt-4 dark:border-blue-900/40">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(`${BASE}.progress.otp_prompt`)}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="wa-otp-inline"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="w-32 rounded-md border border-blue-200 bg-white px-3 py-2 text-center font-mono text-sm dark:border-blue-800/50 dark:bg-gray-800"
                  />
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                    isDisabled={otpCode.length < 6}
                    isLoading={verifyNumber.isPending}
                    onPress={async () => {
                      try {
                        await verifyNumber.mutateAsync({ assistant_id: assistantId, code: otpCode })
                        toast.success(t(`${BASE}.toast.verified`))
                        setOtpCode('')
                        setVerifying(true)
                      } catch (e) {
                        toast.error(t(`${BASE}.toast.verification_failed`, { message: apiErrorMessage(e) }))
                      }
                    }}
                  >
                    {t(`${BASE}.progress.submit_code`)}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </>
    )
  }

  return (
    <>
    <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,600px)_320px]">
    <div>
      <div className="flex items-center gap-2">
        <WhatsAppLogo className="h-8 w-8" />
        <span className="text-gray-300 dark:text-gray-600">×</span>
        <MetaLogo className="h-6 w-auto" />
      </div>
      <h2 className="mt-4 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        {t(`${BASE}.setup.title`)}
      </h2>
      <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
        {t(`${BASE}.setup.subtitle`)}
      </p>

      {/* Mobile warning */}
      <div className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 md:hidden dark:border-amber-800/50 dark:bg-amber-900/10">
        <Monitor className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
        <p className="text-sm text-gray-800 dark:text-gray-200">
          {t(`${BASE}.setup.mobile_warning`)}
        </p>
      </div>

      {/* Safari warning */}
      {isDesktop && isSafari() && (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/50 dark:bg-amber-900/10">
          <Monitor className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
          <p className="text-sm text-gray-800 dark:text-gray-200">
            <Trans i18nKey={`${BASE}.setup.safari_warning`} components={{ strong: <strong /> }} />
          </p>
        </div>
      )}

      {/* Step 1: verify the number with Meta (automatic); Connect unlocks once verified */}
      <div className="mt-6 hidden space-y-4 md:block">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {preverify?.status === 'verified' ? (
            <>
              <p className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                {t(`${BASE}.setup.verified_title`)}
              </p>
              <p className="mt-1 text-sm italic text-gray-500 dark:text-gray-400">{t(`${BASE}.setup.verified`)}</p>
            </>
          ) : (
            <>
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t(`${BASE}.setup.verify_title`, { number: status?.phone_number ?? '…' })}
              </p>
              <p className="mt-1 text-sm italic text-gray-500 dark:text-gray-400">
                {t(`${BASE}.setup.verify_subtitle`)}
              </p>
              <div className="mt-4">
                {preverify?.status === 'armed' || preverify?.status === 'listening' || startPreverify.isPending ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{t(`${BASE}.setup.verifying`)}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(preverify?.status === 'failed' || preverify?.status === 'technical') && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {verifyAttempts >= 2
                          ? t(`${BASE}.setup.verify_failed_support`)
                          : t(`${BASE}.setup.verify_failed`)}
                      </p>
                    )}
                    {preverify?.manual_verification_required && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t(`${BASE}.setup.manual_verification_required`)}
                      </p>
                    )}
                    {(preverify?.status === 'failed' || preverify?.status === 'technical') &&
                    verifyAttempts >= 2 &&
                    calendlyUrl ? (
                      <a
                        href={calendlyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100"
                      >
                        {t(`${BASE}.retry.schedule_meeting`)} →
                      </a>
                    ) : (
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                        isDisabled={preverifyWait > 0 || startPreverify.isPending}
                        isLoading={startPreverify.isPending}
                        onPress={() => {
                          setVerifyAttempts((n) => n + 1)
                          startPreverify.mutate(
                            { assistant_id: assistantId },
                            {
                              onError: (e) => {
                                // The server refused — take its wait, not our guess.
                                const cooldown = preverifyCooldownFromError(e)
                                if (cooldown) {
                                  setPreverifyWait(cooldown.retry_after_seconds)
                                  return
                                }
                                toast.error(apiErrorMessage(e))
                              },
                            },
                          )
                        }}
                      >
                        {preverifyWait > 60
                          ? t(`${BASE}.setup.retry_wait_minutes`, { minutes: Math.ceil(preverifyWait / 60) })
                          : preverifyWait > 0
                            ? t(`${BASE}.setup.retry_wait_seconds`, { seconds: preverifyWait })
                            : preverify?.status === 'failed' || preverify?.status === 'technical'
                              ? t(`${BASE}.setup.try_again`)
                              : t(`${BASE}.setup.verify_button`)}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions row — below the card, right-aligned */}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setDisplayNameAck(false)
              setGateOpen(true)
            }}
            disabled={!sdkLoaded || !metaConfigId || connecting || !isDesktop || preverify?.status !== 'verified'}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
          >
            {connecting ? t(`${BASE}.setup.connecting`) : (
              <>{t(`${BASE}.setup.connect_short`)} <ArrowUpRight className="h-4 w-4" /></>
            )}
          </button>
        </div>

        {/* Display name gate: Meta's popup defaults the display name to the business portfolio name */}
        <Modal isOpen={gateOpen} onOpenChange={setGateOpen} size="md">
          <ModalContent>
            {({ close }) => (
              <>
                <ModalHeader onClose={close}>
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
                    {t(`${BASE}.setup.display_name_title`)}
                  </span>
                </ModalHeader>
                <ModalBody>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <Trans i18nKey={`${BASE}.setup.display_name_warning`} components={{ strong: <strong /> }} />
                  </p>
                  {displayName && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                        {displayName}
                      </span>
                      <button
                        type="button"
                        aria-label={t(`${BASE}.setup.display_name_copy`)}
                        onClick={async () => {
                          await navigator.clipboard.writeText(displayName)
                          toast.success(t(`${BASE}.setup.display_name_copied`))
                        }}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <Checkbox className="mt-4" isSelected={displayNameAck} onChange={setDisplayNameAck}>
                    <span className="text-sm text-gray-900 dark:text-gray-100">{t(`${BASE}.setup.display_name_ack`)}</span>
                  </Checkbox>
                  {calendlyUrl && (
                    <a
                      href={calendlyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100"
                    >
                      {t(`${BASE}.setup.display_name_help`)} →
                    </a>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button type="button" color="secondary" size="sm" onPress={close}>
                    {t('resources.generic.cancel')}
                  </Button>
                  {/* FB.login runs synchronously inside this click, so popup blockers allow it. */}
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                    isDisabled={!displayNameAck}
                    onPress={() => {
                      close()
                      handleConnect()
                    }}
                  >
                    {t(`${BASE}.setup.connect_short`)}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      </div>
    </div>
    <div className="order-first lg:order-none">{sideCard}</div>
    </div>
    </>
  )
}
